import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFetchCategories } from "hooks/useFetchCategories";
import React, { createElement, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import * as ReactIcons from "react-icons/bs";
import { CiBank, CiSquareMinus, CiSquarePlus } from "react-icons/ci";
import * as ReactIconsFa from "react-icons/fa6";
import * as ReactIconsGi from "react-icons/gi";
import * as ReactIconsMd from "react-icons/md";
import CollapseIcon from "../../../../../src/assets/img/icons/collapse.ico";
import ExpandIcon from "../../../../../src/assets/img/icons/expand.ico";
import AddNewChartOfAccount from "./Modals/AddNewChartOfAccount";
import "./styles.css";
import { useFiscalYear } from "context/FiscalYearContext";
import { useFetchCostCenter } from "hooks/useFetchCostCenter";
import { useChartOfAccountContext } from "context/ChartOfAccountContext";
import { useFetchChartOfAccounts } from "hooks/useFetchChartOfAccounts";
import { FaRegFolder } from "react-icons/fa";
import { useSettingContext } from "context/SettingContext";
import PageTitle from "utils/PageTitle";
import BreadCrumbTable from "components/breadcrumb/BreadCrumbTable";

const ChartofAccountsTable = () => {
  const [data, setData] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodeClick, setNodeClick] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const CombinedIcons = {
    ...ReactIcons,
    ...ReactIconsFa,
    ...ReactIconsMd,
    ...ReactIconsGi,
  };

  // const { costCenterId, setCostCenterId, costCenters, isLoading } =
  //   useChartOfAccountContext();

  const settingsData = useSettingContext();
  const costCenter = useMemo(
    () => settingsData?.find((d) => d.name === "DEFAULT_COST_CENTER"),
    [settingsData]
  );
  const costCenterId = Number(costCenter?.value); // Ensure it's a number

  const createHierarchy = (data) => {
    // console.log("data", data);

    const lookup = {};
    const result = [];

    // Initialize lookup table with ids and prepare empty children arrays
    data?.forEach((item) => {
      lookup[item.id] = { ...item, children: [] };
    });

    // Build hierarchy
    data?.forEach((item) => {
      const { id, parentAccountId } = item;
      if (parentAccountId === 0) {
        result.push(lookup[id]);
      } else if (lookup[parentAccountId]) {
        lookup[parentAccountId].children.push(lookup[id]);
      }
    });

    // console.log("result", result);

    return result;
  };

  const { data: chartOfAccountsData, isLoading: chartOfAccountsDataLoading } =
    useFetchChartOfAccounts(costCenterId);

  useEffect(() => {
    const hierarchicalData = createHierarchy(
      chartOfAccountsData?.data?.data || []
    );
    // console.log(hierarchicalData);
    setData(hierarchicalData);
  }, [chartOfAccountsData]);

  const handleExpandAll = () => {
    const allNodeIds = [];
    const traverse = (nodes) => {
      nodes.forEach((node) => {
        allNodeIds.push(node.id);
        if (node.children) {
          traverse(node.children);
        }
      });
    };
    traverse(data);
    setExpanded(allNodeIds);
  };

  const handleCollapseAll = () => {
    setExpanded([]);
  };

  const toggleNode = (nodeId) => {
    setExpanded((prevExpanded) =>
      prevExpanded.includes(nodeId)
        ? prevExpanded.filter((id) => id !== nodeId)
        : [...prevExpanded, nodeId]
    );
  };

  const handleNodeDoubleClick = (node) => {
    setSelectedNode(node);
    setModalOpen(true);
  };

  const handleNodeClick = (node) => {
    setNodeClick(node);
  };

  const filterTree = (nodes) => {
    return nodes
      .map((node) => {
        if (node.title.toLowerCase().includes(searchQuery.toLowerCase())) {
          return node;
        }
        if (node.children) {
          const filteredChildren = filterTree(node.children);
          if (filteredChildren.length) {
            return { ...node, children: filteredChildren };
          }
        }
        return null;
      })
      .filter((node) => node);
  };
  // const { data: costCenterData, isLoading } = useFetchCostCenter();
  // let costCenters = costCenterData?.data?.data;

  const renderTree = (nodes) =>
    nodes.map((node) => (
      <div key={node.id}>
        <div
          className={`d-flex align-items-center cursor-pointer  ${
            expanded.includes(node.id) ? "expanded" : ""
          }`}
          onDoubleClick={() => handleNodeDoubleClick(node)}
          onClick={() => handleNodeClick(node)}
        >
          {node.children && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                toggleNode(node.id);
              }}
            >
              {expanded.includes(node.id) ? (
                <CiSquareMinus style={{ marginRight: "5px" }} />
              ) : (
                <CiSquarePlus style={{ marginRight: "5px" }} />
              )}
            </span>
          )}
          <div
            style={{
              display: "flex ",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              width: "100%",
            }}
          >
            <div className="d-flex align-items-center">
              {node.accountType === "Group" ? (
                <>
                  <ReactIconsMd.MdOutlineFolder size={20} />
                </>
              ) : (
                <>
                  {node.subClass === "Income Account" && (
                    <>
                      <CiBank />
                    </>
                  )}
                </>
              )}
              <span
                // className={`px-2 rounded`}
                className={`${
                  nodeClick?.id === node?.id && "selected-node"
                } px-2 rounded`}
              >
                {node.title}
              </span>
            </div>
            $ {node.amount}
          </div>
        </div>
        {node.children && expanded.includes(node.id) && (
          <div className="tree-node-children" style={{ marginLeft: "40px" }}>
            {renderTree(node.children)}
          </div>
        )}
      </div>
    ));

  useEffect(() => {
    if (searchQuery === "") {
      setExpanded([]);
    } else {
      const matchingNodeIds = [];
      const traverse = (nodes, parentIds = []) => {
        nodes.forEach((node) => {
          if (node.title.toLowerCase().includes(searchQuery.toLowerCase())) {
            matchingNodeIds.push(...parentIds, node.id);
          }
          if (node.children) {
            traverse(node.children, [...parentIds, node.id]);
          }
        });
      };
      traverse(data);
      setExpanded([...new Set(matchingNodeIds)]);
    }
  }, [searchQuery]);

  return (
    <>
      <PageTitle name="  Accounts | Chart of Accounts" />

      <div>
        <Card style={{ height: `calc(100vh - 4rem)` }}>
          <Card.Header className="border-bottom">
            <Row className="align-items-center">
              <Col xs={12} md={7} className=" align-items-center">
                <BreadCrumbTable
                  title="Accounts"
                  subTitle="Chart of Accounts"
                />

                <div className="d-flex gap-3 pt-1">
                  <img
                    src={CollapseIcon}
                    onClick={handleCollapseAll}
                    alt="Collapse"
                    title="Collapse"
                    style={{ width: "15px", cursor: "pointer" }}
                  />
                  <img
                    src={ExpandIcon}
                    onClick={handleExpandAll}
                    alt="Expand"
                    title="Expand"
                    style={{ width: "15px", cursor: "pointer" }}
                  />
                </div>
              </Col>

              <Col
                xs={12}
                md={5}
                className="d-flex justify-content-end align-items-center gap-2"
              >
                <Form className="position-relative">
                  <FontAwesomeIcon
                    icon="search"
                    className="position-absolute text-400 search-box-icon"
                    style={{
                      left: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />
                  <Form.Control
                    type="search"
                    placeholder="Search..."
                    style={{ paddingLeft: "30px" }}
                    aria-label="Search"
                    className="rounded-pill search-input"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </Form>
                <Button
                  variant="falcon-default"
                  size="sm"
                  title="New"
                  className="d-flex align-items-center"
                  onClick={() => setModalOpen(true)}
                >
                  <FontAwesomeIcon icon="plus" className="me-2" />
                  <span className="d-none d-sm-inline">New</span>
                </Button>
              </Col>
            </Row>
          </Card.Header>
          <div className="bg-white p-3" style={{ overflow: "auto" }}>
            {chartOfAccountsDataLoading ? (
              <div className="d-flex justify-content-center align-items-center">
                <Button variant="falcon-default" size="sm" className="fs-10">
                  Loading...
                </Button>
              </div>
            ) : data.length === 0 ? (
              <div className="d-flex justify-content-center">
                Nothing found - Looks empty over here
              </div>
            ) : (
              <div className="tree-view">{renderTree(filterTree(data))}</div>
            )}
          </div>
        </Card>

        <AddNewChartOfAccount
          isOpenModal={modalOpen}
          setIsOpenModal={setModalOpen}
          selectedNode={selectedNode}
          setSelectedNode={setSelectedNode}
        />
      </div>
    </>
  );
};

export default ChartofAccountsTable;

//  <h5 className="fs-9 mb-0 text-nowrap d-flex align-items-center py-2 py-xl-0 ">
//                   Accounts | Chart of Accounts
//                   {/* <div className="mx-2">
//                   <Form.Group>
//                     <Form.Select
//                       aria-label="costCenter"
//                       type="text"
//                       title="Cost Center"
//                       onChange={(event) => setCostCenterId(event.target.value)}
//                       value={costCenterId}
//                     >
//                       <option value="">Select Cost Center</option>
//                       {isLoading ? (
//                         <option value="">Loading...</option>
//                       ) : (
//                         <>
//                           {costCenters?.map((c) => (
//                             <option key={c.id} value={c.id}>
//                               {c.name}
//                             </option>
//                           ))}
//                         </>
//                       )}
//                     </Form.Select>
//                   </Form.Group>
//                 </div> */}
//                 </h5>
